import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Check, Warning } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { formatAuthError } from "../lib/AuthContext";
import { AuthShell, Field } from "./LoginPage";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  if (!token) {
    return (
      <AuthShell title="Invalid link" subtitle="This reset link is missing its token.">
        <Link to="/forgot-password" className="text-tac-primary hover:underline text-sm font-bold">
          Request a new reset link
        </Link>
      </AuthShell>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="You can log in with your new password now.">
        <div data-testid="reset-pw-done" className="flex items-start gap-2.5 p-3 border border-tac-primary/40 bg-tac-primary/8 rounded-sm">
          <Check size={16} weight="fill" className="text-tac-primary shrink-0 mt-0.5" />
          <div className="text-sm text-tac-text">Redirecting to log in…</div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password" subtitle="Choose something at least 8 characters long.">
      <form onSubmit={onSubmit} data-testid="reset-pw-form" className="space-y-4">
        <Field
          icon={<Lock size={14} weight="bold" />}
          label="New password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          minLength={8}
          testId="reset-pw-input"
        />
        {error && (
          <div data-testid="reset-pw-error" className="flex items-start gap-2 text-xs text-tac-danger border border-tac-danger/40 bg-tac-danger/10 rounded-sm px-3 py-2">
            <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        <button
          type="submit"
          disabled={submitting || password.length < 8}
          data-testid="reset-pw-submit"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-tac-primary hover:bg-tac-primary/90 disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs rounded-sm transition-colors"
        >
          {submitting ? "Updating…" : "Update password"}
          {!submitting && <ArrowRight size={14} weight="bold" />}
        </button>
      </form>
    </AuthShell>
  );
}
